import React from 'react';
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2, Users, StickyNote, Clock3, UserPlus, Pencil, RotateCcw } from 'lucide-react';
import StaffingTargetPicker from './StaffingTargetPicker';
import Combobox from './ui/combobox';

// Searchable recommendations for common disaster and incident-command roles.
// These are suggestions only; planners can still create a custom role.
export const ROLE_OPTIONS = [
  'Incident Commander',
  'Deputy Incident Commander',
  'Safety Officer',
  'Liaison Officer',
  'Public Information Officer',
  'Emergency Operations Center Manager',
  'Operations Section Chief',
  'Planning Section Chief',
  'Logistics Section Chief',
  'Finance and Administration Section Chief',
  'Fire and Rescue Lead',
  'Search and Rescue Lead',
  'EMS Lead',
  'Medical Officer',
  'Triage Lead',
  'Paramedic',
  'Hazmat Specialist',
  'Evacuation Lead',
  'Shelter Coordinator',
  'Security Lead',
  'Communications Lead',
  'Dispatcher',
  'Situation Unit Leader',
  'Resource Unit Leader',
  'Damage Assessment Lead',
  'GIS and Mapping Lead',
  'Transportation Coordinator',
  'Supply and Logistics Coordinator',
  'Volunteer Coordinator',
  'Family Assistance Lead',
  'Recovery Coordinator',
  'Field Officer',
];

export const blankRole = () => ({ role: '', count: 1, instructions: '' });

export const getRoleInstructions = (role, legacyNote = '') => {
  if (role && Object.prototype.hasOwnProperty.call(role, 'instructions')) {
    return String(role.instructions || '').trim();
  }
  return String(legacyNote || '').trim();
};

const cleanStaffingRefs = (values = []) => {
  const seen = new Set();
  return (values || []).filter((row) => {
    const key = `${row?.type || ''}:${row?.id || ''}`;
    if (!row?.type || !row?.id || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((row) => ({
    type: String(row.type),
    id: String(row.id),
    name: String(row.name || ''),
  }));
};

export const cleanCardStaffing = (staffing, fallbackCount = 1) => {
  if (!staffing || typeof staffing !== 'object') return undefined;
  return {
    mode: 'fill_slots',
    required_count: Math.max(1, Number(staffing.required_count) || Number(fallbackCount) || 1),
    preferred: cleanStaffingRefs([
      ...(Array.isArray(staffing.preferred) ? staffing.preferred : []),
      ...(Array.isArray(staffing.fallbacks) ? staffing.fallbacks : []),
    ]),
    fallbacks: [],
  };
};

// Normalise a roles array: keep rows with a role name, coerce count.
export const cleanRoles = (roles = []) =>
  (roles || [])
    .filter((r) => r && String(r.role || '').trim())
    .map((r) => {
      const count = Math.max(1, parseInt(r.count, 10) || 1);
      const normalizedStaffing = cleanCardStaffing(r.staffing, count);
      const staffing = normalizedStaffing
        ? { ...normalizedStaffing, required_count: count }
        : undefined;
      const hasInstructions = Object.prototype.hasOwnProperty.call(r, 'instructions');
      return {
        role: String(r.role).trim(),
        count,
        ...(hasInstructions ? { instructions: String(r.instructions || '').trim() } : {}),
        ...(staffing ? { staffing } : {}),
        ...(typeof r.pulledFromScenario === 'boolean'
          ? { pulledFromScenario: r.pulledFromScenario }
          : {}),
      };
    });

export const rolesValid = (roles = [], roleCatalog = null) => {
  const clean = cleanRoles(roles);
  if (!clean.length) return false;
  if (!roleCatalog || typeof roleCatalog?.roles !== 'object') return true;
  const allowed = new Set(Object.keys(roleCatalog.roles));
  return clean.every((row) => allowed.has(row.role));
};

// Compact "2× Paramedic, 1× Fire Captain" summary string.
export const rolesSummary = (roles = []) =>
  cleanRoles(roles).map((r) => `${r.count}× ${r.role}`).join(', ');

export const canOverrideScenarioRoles = (incident, item) => {
  if (!item?.meta?.plannedFromScenarioId || incident?.is_scenario) return false;
  if (incident?.is_exercise) {
    return String(incident?.status || '').toLowerCase() === 'requested';
  }
  return incident?.plan_state === 'planned' && item?.plan_state === 'planned';
};

const hasSavedScenarioCandidates = (role) => Boolean([
  ...(Array.isArray(role?.staffing?.preferred) ? role.staffing.preferred : []),
  ...(Array.isArray(role?.staffing?.fallbacks) ? role.staffing.fallbacks : []),
].some((candidate) => candidate?.type && candidate?.id));

export const markScenarioRolesForOverride = (roles = [], enabled = false) => (roles || []).map((role) => {
  if (!enabled) return { ...role };
  // `pulledFromScenario` is stamped by the backend for new incidents/drills.
  // Candidate-backed staffing is the narrow legacy fallback for cards pulled
  // before that marker existed; roles added later in the runtime editor do not
  // inherit the source card's staffing snapshot.
  const pulledFromScenario = role?.pulledFromScenario === true
    || (role?.pulledFromScenario == null && hasSavedScenarioCandidates(role));
  return {
    ...role,
    ...(pulledFromScenario ? {
      pulledFromScenario: true,
      _scenarioDefaultDefinitionLocked: true,
    } : {}),
  };
});

const assignmentKey = (person = {}, index = 0) => String(
  person?.id || person?.userId || person?.user_id || person?.email || `row-${index}`
);

const uniqueAssignments = (people = []) => {
  const seen = new Set();
  return (Array.isArray(people) ? people : []).filter((person, index) => {
    const key = assignmentKey(person, index).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const uniqueCandidates = (people = []) => {
  const seen = new Set();
  return (Array.isArray(people) ? people : []).filter((person) => {
    const key = `${person?.type || ''}:${person?.id || ''}`.toLowerCase();
    if (!person?.type || !person?.id || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const assignmentEntriesForRole = (assignments, roleName) => Object.entries(assignments || {})
  .filter(([name]) => name.toLowerCase() === String(roleName || '').toLowerCase());

const removeAssignmentRole = (assignments, roleName) => {
  assignmentEntriesForRole(assignments, roleName).forEach(([name]) => { delete assignments[name]; });
};

const combineInstructions = (first = '', second = '') => {
  const values = [first, second].map((value) => String(value || '').trim()).filter(Boolean);
  return [...new Set(values)].join('\n');
};

const currentOverrideActor = () => {
  if (typeof window === 'undefined') return null;
  try {
    const user = JSON.parse(window.localStorage.getItem('user') || 'null');
    if (!user) return null;
    return {
      id: user.id || user.userId || user.email || null,
      name: user.full_name || user.fullName || user.name || user.email || 'Administrator',
    };
  } catch {
    return null;
  }
};

// Finalise UI-only scenario-role overrides into one consistent card meta patch.
// The role definition and assignment key move together so an old role can never
// remain visible to responders after a rename.
export const buildScenarioRoleOverridePatch = (originalMeta = {}, draftRoles = []) => {
  const entries = (draftRoles || [])
    .filter((row) => row && String(row.role || '').trim())
    .map((draft) => ({ draft, clean: cleanRoles([draft])[0] }));
  const assignments = { ...(originalMeta?.roleAssignments || {}) };
  const auditRows = [];
  const changedBy = currentOverrideActor();

  for (const entry of [...entries]) {
    const row = entry.draft;
    if (!row?._scenarioRoleOverrideEditing) continue;
    const oldRole = String(row._scenarioOriginalRole || '').trim();
    const newRole = String(entry.clean?.role || '').trim();
    const oldCount = Math.max(1, Number(row._scenarioOriginalCount) || 1);
    const newCount = Math.max(1, Number(entry.clean?.count) || 1);
    if (!oldRole || !newRole) return { error: 'Choose a role before saving the override.' };

    const sourcePeople = uniqueAssignments(
      assignmentEntriesForRole(assignments, oldRole).flatMap(([, people]) => Array.isArray(people) ? people : [])
    );
    let retainedPeople = sourcePeople;
    if (newCount < sourcePeople.length) {
      const requested = new Set((row._retainedAssigneeKeys || []).map((key) => String(key).toLowerCase()));
      retainedPeople = sourcePeople.filter((person, index) => requested.has(assignmentKey(person, index).toLowerCase()));
      if (retainedPeople.length !== newCount) {
        return { error: `Choose exactly ${newCount} ${newCount === 1 ? 'person' : 'people'} to keep for ${oldRole}.` };
      }
    }

    const duplicate = entries.find((candidate) => candidate !== entry
      && candidate.clean.role.toLowerCase() === newRole.toLowerCase());
    if (duplicate && !row._mergeConfirmed) {
      return { error: `${newRole} is already required on this card. Confirm the role merge before saving.` };
    }

    if (row._candidateDecision === 'reset') delete entry.clean.staffing;
    removeAssignmentRole(assignments, oldRole);

    if (duplicate) {
      const targetPeople = uniqueAssignments(
        assignmentEntriesForRole(assignments, duplicate.clean.role)
          .flatMap(([, people]) => Array.isArray(people) ? people : [])
      );
      removeAssignmentRole(assignments, duplicate.clean.role);
      assignments[duplicate.clean.role] = uniqueAssignments([...targetPeople, ...retainedPeople]);
      duplicate.clean = {
        ...duplicate.clean,
        count: duplicate.clean.count + entry.clean.count,
        instructions: combineInstructions(duplicate.clean.instructions, entry.clean.instructions),
        ...((duplicate.clean.pulledFromScenario === true || entry.clean.pulledFromScenario === true)
          ? { pulledFromScenario: true }
          : {}),
      };
      const targetCandidates = duplicate.clean.staffing?.preferred || [];
      const sourceCandidates = row._candidateDecision === 'reset' ? [] : (entry.clean.staffing?.preferred || []);
      if (duplicate.clean.staffing || sourceCandidates.length) {
        duplicate.clean.staffing = {
          mode: 'fill_slots',
          required_count: duplicate.clean.count,
          preferred: uniqueCandidates([...targetCandidates, ...sourceCandidates]),
          fallbacks: [],
        };
      }
      entries.splice(entries.indexOf(entry), 1);
    } else {
      assignments[newRole] = retainedPeople;
    }

    const changed = oldRole !== newRole
      || oldCount !== newCount
      || row._candidateDecision === 'reset';
    if (changed) {
      auditRows.push({
        type: 'scenario_role_override',
        changedAt: new Date().toISOString(),
        originalRole: oldRole,
        role: newRole,
        originalCount: oldCount,
        count: duplicate ? duplicate.clean.count : newCount,
        candidateDecision: row._candidateDecision === 'reset' ? 'reset' : 'keep',
        mergedIntoExistingRole: Boolean(duplicate),
        ...(changedBy ? { changedBy } : {}),
      });
    }
  }

  const roleOverrideHistory = [
    ...(Array.isArray(originalMeta?.roleOverrideHistory) ? originalMeta.roleOverrideHistory : []),
    ...auditRows,
  ];
  const history = [
    ...(Array.isArray(originalMeta?.history) ? originalMeta.history : []),
    ...auditRows,
  ];
  // Fail closed for cards that already contain stale/orphaned assignment keys.
  // Only current required roles survive, in the same canonical order as the
  // card role list, so the web timeline and mobile app cannot expose an old role.
  const canonicalAssignments = {};
  entries.forEach(({ clean }) => {
    canonicalAssignments[clean.role] = uniqueAssignments(
      assignmentEntriesForRole(assignments, clean.role)
        .flatMap(([, people]) => Array.isArray(people) ? people : [])
    );
  });
  return {
    scenarioRoles: entries.map(({ clean }) => clean),
    roleAssignments: canonicalAssignments,
    history,
    roleOverrideHistory,
    changed: auditRows.length > 0,
  };
};

// Editable multi-role picker shown at the top of inject modals on the SCENARIO page.
// Scenario cards can only use roles defined in Required Roles. Each card stores
// its own selected/reordered candidate snapshot so future scenario edits cannot
// change an already-instantiated incident or drill.
export default function ScenarioRoleField({
  roles = [],
  setRoles,
  note = '',
  slaValue,
  onSlaChange,
  slaLabel = 'SLA (minutes)',
  slaReadOnly = false,
  slaHint = '',
  noteReadOnly = false,
  slaZeroLabel = '(0 = immediate)',
  roleCatalog = null,
  onRoleCatalogUpsert = null,
  lockScenarioDefaultRoles = false,
  roleAssignments = {},
}) {
  const [catalogSaveState, setCatalogSaveState] = React.useState({ role: '', status: '', message: '' });
  const [expandedCandidates, setExpandedCandidates] = React.useState({});
  const [candidatePicker, setCandidatePicker] = React.useState(null);
  const safeRoles = roles && roles.length ? roles : [blankRole()];
  const catalogRoles = roleCatalog?.roles && typeof roleCatalog.roles === 'object' ? roleCatalog.roles : null;
  const catalogEntries = Object.entries(catalogRoles || {});
  const catalogMode = !!catalogRoles;
  const catalogEditable = catalogMode && typeof onRoleCatalogUpsert === 'function';
  const update = (i, k, v) => setRoles(safeRoles.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => setRoles([
    ...safeRoles,
    {
      ...blankRole(),
      ...(lockScenarioDefaultRoles ? { pulledFromScenario: false } : {}),
    },
  ]);
  const remove = (i) => setRoles(safeRoles.length > 1 ? safeRoles.filter((_, idx) => idx !== i) : safeRoles);

  const catalogSignature = JSON.stringify(catalogRoles || {});

  React.useEffect(() => {
    if (!catalogMode) return;
    setRoles((currentRoles) => {
      let changed = false;
      const current = currentRoles?.length ? currentRoles : [blankRole()];
      const next = current.map((row) => {
        const cfg = catalogRoles[row.role];
        if (!cfg) return row;
        const count = Math.max(1, Number(row.count) || 1);
        const staffing = row.staffing ? cleanCardStaffing(row.staffing, count) : cleanCardStaffing(cfg, count);
        const cardStaffing = staffing ? { ...staffing, required_count: count } : staffing;
        if (!row.staffing || Number(row.staffing?.required_count) !== count) changed = true;
        return { ...row, staffing: cardStaffing };
      });
      return changed ? next : currentRoles;
    });
  }, [catalogMode, catalogSignature, setRoles]);

  const selectRole = (index, roleName) => {
    const cfg = catalogRoles?.[roleName];
    const current = safeRoles[index] || blankRole();
    const count = Math.max(1, Number(current.count) || 1);
    const sameRole = current.role === roleName;
    const staffing = cleanCardStaffing(sameRole ? current.staffing || cfg : cfg, count);
    setRoles(safeRoles.map((row, idx) => idx === index
      ? {
        ...row,
        role: roleName,
        count,
        instructions: sameRole ? String(row.instructions || '') : '',
        staffing: staffing ? { ...staffing, required_count: count } : staffing,
      }
      : row));
  };

  const roleOptionsForIndex = (index) => {
    const editingOverride = Boolean(safeRoles[index]?._scenarioRoleOverrideEditing);
    const selectedElsewhere = new Set(safeRoles
      .filter((_, roleIndex) => roleIndex !== index)
      .map((row) => String(row?.role || '').trim().toLowerCase())
      .filter(Boolean));
    const catalogKeys = new Set(catalogEntries.map(([name]) => name.toLowerCase()));
    const cardRoles = editingOverride
      ? safeRoles
        .filter((_, roleIndex) => roleIndex !== index)
        .map((row) => String(row?.role || '').trim())
        .filter(Boolean)
        .map((name) => ({ value: name, label: name, group: 'Roles on this card' }))
      : [];
    const all = [
      ...cardRoles,
      ...catalogEntries
        .filter(([name]) => editingOverride || !selectedElsewhere.has(name.toLowerCase()))
        .map(([name]) => ({ value: name, label: name, group: 'Required Roles' })),
      ...ROLE_OPTIONS
        .filter((name) => !catalogKeys.has(name.toLowerCase()) && (editingOverride || !selectedElsewhere.has(name.toLowerCase())))
        .map((name) => ({ value: name, label: name, group: 'Recommended disaster roles' })),
    ];
    const seen = new Set();
    return all.filter((option) => {
      const key = option.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const chooseEditableRole = async (index, value) => {
    const overrideEditing = Boolean(safeRoles[index]?._scenarioRoleOverrideEditing);
    if ((!catalogMode || typeof onRoleCatalogUpsert !== 'function') && !overrideEditing) return;
    const role = String(value || '').trim();
    const row = safeRoles[index] || blankRole();
    const count = Math.max(1, Math.min(100, Number(row?.count) || 1));
    if (!role) return;
    if (role.includes('.') || role.startsWith('$')) {
      setCatalogSaveState({ role, status: 'error', message: 'Role names cannot contain “.” or start with “$”.' });
      return;
    }
    const duplicateRole = safeRoles.find((item, roleIndex) => roleIndex !== index && String(item?.role || '').trim().toLowerCase() === role.toLowerCase());
    if (duplicateRole && !overrideEditing) {
      setCatalogSaveState({ role, status: 'error', message: 'This role is already used on this card. Increase its slot count instead.' });
      return;
    }
    const existingName = catalogEntries.find(([name]) => name.toLowerCase() === role.toLowerCase())?.[0];
    const savedRole = existingName || role;
    const cfg = existingName ? catalogRoles[existingName] : null;
    const sameRole = row.role === savedRole;
    const staffingSource = overrideEditing
      ? (row._candidateDecision === 'reset' ? undefined : (row.staffing || row._scenarioOriginalStaffing))
      : (sameRole ? row.staffing || cfg : cfg);
    const staffing = cleanCardStaffing(staffingSource, count);
    setRoles(safeRoles.map((item, idx) => idx === index ? {
      ...item,
      role: savedRole,
      count,
      instructions: (sameRole || overrideEditing) ? String(item.instructions || '') : '',
      staffing: staffing ? { ...staffing, required_count: count } : undefined,
      ...(overrideEditing ? { _mergeConfirmed: false } : {}),
    } : item));
    if (overrideEditing) {
      setCatalogSaveState({
        role: savedRole,
        status: 'saved',
        message: duplicateRole ? 'This will merge with the existing role after confirmation.' : 'Override selected for this card only.',
      });
      return;
    }
    if (existingName) {
      setCatalogSaveState({ role: savedRole, status: 'saved', message: 'Selected from Required Roles' });
      return;
    }
    setCatalogSaveState({ role: savedRole, status: 'saving', message: 'Saving to Required Roles…' });
    try {
      await onRoleCatalogUpsert({ role: savedRole });
      setCatalogSaveState({ role: savedRole, status: 'saved', message: 'Saved to Required Roles' });
    } catch (error) {
      setCatalogSaveState({ role: savedRole, status: 'error', message: error?.message || 'Unable to update Required Roles.' });
    }
  };

  const updateStaffing = (index, patch) => {
    const row = safeRoles[index];
    update(index, 'staffing', { ...(cleanCardStaffing(row.staffing, row.count) || {}), ...patch });
  };

  const updateCardCount = (index, value) => {
    setRoles(safeRoles.map((row, idx) => {
      if (idx !== index) return row;
      const count = Math.max(1, Math.min(100, Number(value) || 1));
      const staffing = row.staffing
        ? { ...(cleanCardStaffing(row.staffing, count) || {}), required_count: count }
        : row.staffing;
      return { ...row, count: value, ...(staffing ? { staffing } : {}) };
    }));
  };

  const normalizeCardCount = (index) => {
    const row = safeRoles[index];
    const count = Math.max(1, Math.min(100, Number(row?.count) || 1));
    updateCardCount(index, count);
  };

  const startRoleOverride = (index) => {
    setRoles(safeRoles.map((row, roleIndex) => roleIndex === index ? {
      ...row,
      _scenarioRoleOverrideEditing: true,
      _scenarioOriginalRole: String(row.role || '').trim(),
      _scenarioOriginalCount: Math.max(1, Number(row.count) || 1),
      _scenarioOriginalStaffing: row.staffing,
      _candidateDecision: 'keep',
      _retainedAssigneeKeys: [],
      _mergeConfirmed: false,
    } : row));
  };

  const cancelRoleOverride = (index) => {
    setCatalogSaveState({ role: '', status: '', message: '' });
    setRoles(safeRoles.map((row, roleIndex) => {
      if (roleIndex !== index) return row;
      const restored = {
        ...row,
        role: row._scenarioOriginalRole,
        count: row._scenarioOriginalCount,
        staffing: row._scenarioOriginalStaffing,
      };
      delete restored._scenarioRoleOverrideEditing;
      delete restored._scenarioOriginalRole;
      delete restored._scenarioOriginalCount;
      delete restored._scenarioOriginalStaffing;
      delete restored._candidateDecision;
      delete restored._retainedAssigneeKeys;
      delete restored._mergeConfirmed;
      return restored;
    }));
  };

  const setCandidateDecision = (index, decision) => {
    setRoles(safeRoles.map((row, roleIndex) => roleIndex === index ? {
      ...row,
      _candidateDecision: decision,
      staffing: decision === 'reset' ? undefined : row._scenarioOriginalStaffing,
    } : row));
  };

  const toggleRetainedAssignee = (index, key) => {
    setRoles(safeRoles.map((row, roleIndex) => {
      if (roleIndex !== index) return row;
      const current = Array.isArray(row._retainedAssigneeKeys) ? row._retainedAssigneeKeys : [];
      return {
        ...row,
        _retainedAssigneeKeys: current.includes(key)
          ? current.filter((value) => value !== key)
          : [...current, key],
      };
    }));
  };

  const moveCandidate = (roleIndex, kind, candidateIndex, delta) => {
    const selected = [...(safeRoles[roleIndex]?.staffing?.[kind] || [])];
    const target = candidateIndex + delta;
    if (target < 0 || target >= selected.length) return;
    [selected[candidateIndex], selected[target]] = [selected[target], selected[candidateIndex]];
    updateStaffing(roleIndex, { [kind]: selected });
  };

  const removeCandidate = (roleIndex, kind, candidateIndex) => {
    updateStaffing(roleIndex, {
      [kind]: (safeRoles[roleIndex]?.staffing?.[kind] || []).filter((_, index) => index !== candidateIndex),
    });
  };

  const addCandidate = (roleIndex, kind, target) => {
    if (!target?.type || !target?.id) return;
    const selected = safeRoles[roleIndex]?.staffing?.[kind] || [];
    if (selected.some((row) => row.type === target.type && String(row.id) === String(target.id))) return;
    updateStaffing(roleIndex, { [kind]: [...selected, target] });
  };

  const CandidateList = ({ roleIndex, kind, label }) => {
    const selected = safeRoles[roleIndex]?.staffing?.[kind] || [];
    return (
      <div className="rounded-lg border border-violet-200 bg-white/80 p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">{label}</p>
        <div className="mt-1.5 space-y-1">
          {selected.map((row, candidateIndex) => (
            <div key={`${row.type}:${row.id}`} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-600 px-1 text-[10px] font-bold text-white">{candidateIndex + 1}</span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700">{row.name || row.id}</span>
              <span className="text-[9px] font-bold uppercase text-slate-400">{row.type}</span>
              <button type="button" aria-label="Move candidate up" disabled={candidateIndex === 0} onClick={() => moveCandidate(roleIndex, kind, candidateIndex, -1)} className="text-slate-400 disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" aria-label="Move candidate down" disabled={candidateIndex === selected.length - 1} onClick={() => moveCandidate(roleIndex, kind, candidateIndex, 1)} className="text-slate-400 disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" aria-label="Remove candidate" onClick={() => removeCandidate(roleIndex, kind, candidateIndex)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {!selected.length && <p className="text-[10px] italic text-slate-400">No candidates selected for this card.</p>}
        </div>
        <button
          type="button"
          onClick={() => setCandidatePicker({ roleIndex, kind })}
          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-violet-200 bg-white px-3 py-2 text-[11px] font-bold text-violet-700 hover:border-violet-400 hover:bg-violet-50"
          data-testid={`scenario-role-add-candidate-${roleIndex}`}
        >
          <Plus className="h-3.5 w-3.5" /> Add candidates
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-3 p-4 rounded-lg border border-violet-200 bg-violet-50" data-testid="scenario-role-field">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-violet-600">
          <Users className="h-3.5 w-3.5" />
          Required roles <span className="text-rose-500">*</span>
        </div>
        <p className="text-[10px] text-violet-500">Role slots are the people required for this card.</p>
        {safeRoles.map((r, i) => {
          const scenarioDefinition = lockScenarioDefaultRoles
            && Boolean(r?._scenarioDefaultDefinitionLocked);
          const overrideEditing = scenarioDefinition && Boolean(r?._scenarioRoleOverrideEditing);
          const definitionLocked = scenarioDefinition && !overrideEditing;
          const definitionReadOnly = definitionLocked || (catalogMode && !catalogEditable && !overrideEditing);
          const instructionsValue = Object.prototype.hasOwnProperty.call(r, 'instructions')
            ? r.instructions
            : note;
          const originalRole = String(r._scenarioOriginalRole || r.role || '').trim();
          const assignedPeople = uniqueAssignments(
            assignmentEntriesForRole(roleAssignments, originalRole)
              .flatMap(([, people]) => Array.isArray(people) ? people : [])
          );
          const normalizedCount = Math.max(1, Number(r.count) || 1);
          const needsRetentionChoice = overrideEditing && normalizedCount < assignedPeople.length;
          const duplicateTarget = overrideEditing && safeRoles.some((other, roleIndex) => roleIndex !== i
            && String(other?.role || '').trim().toLowerCase() === String(r.role || '').trim().toLowerCase());
          const showCandidateDefaults = Boolean(r.role && (
            (catalogMode && catalogRoles[r.role])
            || (lockScenarioDefaultRoles && r.staffing)
          ));
          return (
          <div key={i} className="space-y-2" data-testid={`scenario-role-row-${i}`}>
            <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="100"
              value={r.count}
              onChange={(e) => { if (!definitionReadOnly) updateCardCount(i, e.target.value); }}
              onBlur={() => { if (!definitionReadOnly) normalizeCardCount(i); }}
              readOnly={definitionReadOnly}
              aria-readonly={definitionReadOnly}
              title={definitionLocked ? 'This role and slot count came from the scenario and cannot be changed here.' : 'People required for this card'}
              data-testid={`scenario-role-count-${i}`}
              className={`w-16 text-sm border border-violet-300 rounded-md px-2 py-2 text-center ${definitionReadOnly ? 'bg-violet-100 text-violet-700 cursor-not-allowed' : 'text-gray-900 bg-white'}`}
            />
            <div className="relative flex-1">
              {(catalogEditable || overrideEditing) ? (
                <Combobox
                  value={r.role}
                  onValueChange={(roleName) => { if (!definitionLocked) chooseEditableRole(i, roleName); }}
                  options={roleOptionsForIndex(i)}
                  disabled={definitionLocked}
                  placeholder="Search Required Roles or recommendations…"
                  searchPlaceholder="Search roles or type a custom role…"
                  createLabel="Create custom role"
                  emptyLabel="No matching role"
                  className={`h-[38px] w-full border-violet-300 px-2.5 text-sm ${definitionLocked ? 'bg-violet-100 text-violet-700' : 'bg-white text-gray-900 hover:bg-white'}`}
                  testId={`scenario-role-input-${i}`}
                />
              ) : catalogMode ? (
                <select value={catalogRoles[r.role] ? r.role : ''} onChange={(event) => selectRole(i, event.target.value)} disabled={definitionLocked} aria-readonly={definitionLocked} data-testid={`scenario-role-input-${i}`} className={`w-full text-sm border border-violet-300 rounded-md px-2.5 py-2 ${definitionLocked ? 'bg-violet-100 text-violet-700 cursor-not-allowed' : 'text-gray-900 bg-white'}`}>
                  <option value="">Select a role from Required Roles…</option>
                  {catalogEntries.map(([name]) => <option key={name} value={name}>{name}</option>)}
                </select>
              ) : (
                <input value={r.role} onChange={(e) => { if (!definitionLocked) update(i, 'role', e.target.value); }} readOnly={definitionLocked} aria-readonly={definitionLocked} title={definitionLocked ? 'This role came from the scenario and cannot be changed here.' : undefined} placeholder="Role" data-testid={`scenario-role-input-${i}`} className={`w-full text-sm border border-violet-300 rounded-md px-2.5 py-2 ${definitionLocked ? 'bg-violet-100 text-violet-700 cursor-not-allowed' : 'text-gray-900 bg-white'}`} />
              )}
            </div>
            {scenarioDefinition ? (
              <button
                type="button"
                onClick={() => (overrideEditing ? cancelRoleOverride(i) : startRoleOverride(i))}
                title={overrideEditing ? 'Cancel role override' : 'Override scenario role'}
                aria-label={overrideEditing ? 'Cancel role override' : 'Override scenario role'}
                data-testid={overrideEditing ? `scenario-role-override-cancel-${i}` : `scenario-role-override-${i}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-2 text-[11px] font-bold text-violet-700 hover:bg-violet-100 hover:text-violet-900"
              >
                {overrideEditing ? <RotateCcw className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                <span>{overrideEditing ? 'Cancel' : 'Override'}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={safeRoles.length <= 1}
                title="Delete role"
                data-testid={`scenario-role-remove-${i}`}
                className="p-2 text-violet-400 hover:text-rose-500 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            </div>
            {overrideEditing && (
              <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3 sm:ml-[72px]" data-testid={`scenario-role-override-panel-${i}`}>
                <fieldset>
                  <legend className="text-[10px] font-bold uppercase tracking-wide text-amber-800">Recommended candidates</legend>
                  <p className="mt-0.5 text-[10px] text-amber-700">Choose what happens to this role’s scenario recommendations. Current assignees stay assigned.</p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {[
                      ['keep', 'Keep recommended candidates'],
                      ['reset', 'Reset recommended candidates'],
                    ].map(([value, label]) => (
                      <label key={value} className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-amber-950">
                        <input type="radio" name={`candidate-decision-${i}`} checked={(r._candidateDecision || 'keep') === value} onChange={() => setCandidateDecision(i, value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                {duplicateTarget && (
                  <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-300 bg-white p-2 text-[11px] text-amber-950">
                    <input type="checkbox" className="mt-0.5" checked={Boolean(r._mergeConfirmed)} onChange={(event) => update(i, '_mergeConfirmed', event.target.checked)} />
                    <span><strong>Merge with the existing {r.role} role.</strong> Slot counts will be added, and duplicate assignees and candidates will be removed.</span>
                  </label>
                )}

                {needsRetentionChoice && (
                  <fieldset className="rounded-md border border-rose-300 bg-white p-2" data-testid={`scenario-role-retain-assignees-${i}`}>
                    <legend className="px-1 text-[10px] font-bold uppercase tracking-wide text-rose-700">Choose exactly {normalizedCount} to keep</legend>
                    <p className="mb-1.5 text-[10px] text-rose-600">The new slot count is lower than the number currently assigned. Saving is blocked until you choose who remains.</p>
                    <div className="space-y-1">
                      {assignedPeople.map((person, personIndex) => {
                        const key = assignmentKey(person, personIndex);
                        const label = person?.name || person?.userName || person?.email || person?.id || person?.userId || 'Assigned responder';
                        return (
                          <label key={key} className="flex cursor-pointer items-center gap-2 text-[11px] font-medium text-slate-700">
                            <input type="checkbox" checked={(r._retainedAssigneeKeys || []).includes(key)} onChange={() => toggleRetainedAssignee(i, key)} />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}
              </div>
            )}
            {catalogEditable && catalogSaveState.role === String(r.role || '').trim() && catalogSaveState.message && (
              <p className={`ml-[72px] text-[10px] font-medium ${catalogSaveState.status === 'error' ? 'text-rose-600' : catalogSaveState.status === 'saving' ? 'text-violet-500' : 'text-emerald-600'}`} aria-live="polite">
                {catalogSaveState.message}
              </p>
            )}
            {!lockScenarioDefaultRoles && (
            <div className="rounded-lg border border-violet-200 bg-white/80 p-2.5 sm:ml-[72px]" data-testid={`scenario-role-instructions-${i}`}>
              <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-600">
                <StickyNote className="h-3.5 w-3.5" />
                Assignment instructions{r.role ? ` — ${r.role}` : ''}
              </label>
              <textarea
                value={instructionsValue || ''}
                onChange={(event) => { if (!noteReadOnly) update(i, 'instructions', event.target.value); }}
                rows={2}
                readOnly={noteReadOnly}
                placeholder={noteReadOnly ? 'No instructions were written for this role.' : 'e.g. Prefer the closest on-duty responder with the required certification.'}
                className={`mt-1 w-full rounded-md border px-2.5 py-2 text-sm ${noteReadOnly ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500' : 'border-violet-300 bg-white text-gray-900'}`}
                data-testid={`scenario-role-instructions-input-${i}`}
              />
              {!noteReadOnly && <p className="mt-1 text-[10px] text-violet-500">Saved with this role for incident and drill staffing review.</p>}
            </div>
            )}
            {!lockScenarioDefaultRoles && showCandidateDefaults && (
              <div className="overflow-hidden rounded-lg border border-violet-200 bg-violet-100/50" data-testid={`scenario-role-staffing-${i}`}>
                <button
                  type="button"
                  onClick={() => setExpandedCandidates((current) => ({ ...current, [i]: !current[i] }))}
                  aria-expanded={!!expandedCandidates[i]}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-violet-100"
                  data-testid={`scenario-role-staffing-toggle-${i}`}
                >
                  <UserPlus className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[10px] font-bold uppercase tracking-wide text-violet-700">Default candidates for this card</span>
                    <span className="block text-[10px] font-medium text-violet-500">{r.staffing?.preferred?.length || 0} selected</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-violet-500 transition-transform ${expandedCandidates[i] ? 'rotate-180' : ''}`} />
                </button>
                {expandedCandidates[i] && (
                  <div className="space-y-2 border-t border-violet-200 px-3 pb-3 pt-2.5">
                    <p className="text-[10px] text-violet-600">Manage candidates for this card here. After you save, Required Roles shows a read-only summary of candidates used across all cards.</p>
                    <CandidateList roleIndex={i} kind="preferred" label="Ordered candidates" />
                  </div>
                )}
              </div>
            )}
            {catalogMode && r.role && !catalogRoles[r.role] && !catalogEditable && <p className="ml-[72px] text-[11px] font-medium text-rose-600">This role is not in Required Roles. Select an available role before saving.</p>}
          </div>
          );
        })}
        <button
          type="button"
          onClick={add}
          data-testid="scenario-role-add"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-violet-700 hover:text-violet-900"
        >
          <Plus className="h-3.5 w-3.5" /> Add another role
        </button>
      </div>

      {onSlaChange && (
        <div>
          <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-600">
            <Clock3 className="h-3.5 w-3.5" />
            {slaLabel} {!slaReadOnly && <span className="normal-case font-normal text-violet-400">{slaZeroLabel}</span>}
          </label>
          <input
            type="number"
            min="0"
            value={slaValue ?? ''}
            onChange={(e) => { if (!slaReadOnly) onSlaChange(e.target.value); }}
            readOnly={slaReadOnly}
            placeholder="e.g. 5"
            data-testid="scenario-role-sla"
            title={slaReadOnly ? 'Auto-calculated from the task SLAs below' : undefined}
            className={`w-full text-sm border rounded-md px-2.5 py-2 mt-1 ${slaReadOnly ? 'border-violet-200 bg-violet-100 text-violet-700 cursor-not-allowed' : 'border-violet-300 bg-white text-gray-900'}`}
          />
          {slaReadOnly && slaHint && (
            <p className="mt-1 text-[10px] text-violet-500">{slaHint}</p>
          )}
        </div>
      )}

      <StaffingTargetPicker
        open={candidatePicker !== null}
        title={`Add candidates — ${candidatePicker !== null ? safeRoles[candidatePicker.roleIndex]?.role || '' : ''}`}
        description="Choose users, departments, or crews for this scenario card. Use the duty filters to find available people quickly."
        selected={candidatePicker !== null ? (safeRoles[candidatePicker.roleIndex]?.staffing?.[candidatePicker.kind] || []) : []}
        onSelect={(target) => candidatePicker !== null && addCandidate(candidatePicker.roleIndex, candidatePicker.kind, target)}
        onClose={() => setCandidatePicker(null)}
      />
    </div>
  );
}

// Read-only guidance shown on instantiated cards so the planner knows which
// roles/instructions were defined during scenario planning.
export function RoleGuidanceBanner({ roles = [], note = '' }) {
  const clean = cleanRoles(roles);
  if (!clean.length && !note) return null;
  return (
    <div className="space-y-2 p-3 rounded-lg border border-violet-200 bg-violet-50" data-testid="role-guidance-banner">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-600">
        <Users className="h-3.5 w-3.5" /> Planned roles to assign
      </div>
      {clean.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {clean.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-violet-300 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
              {r.count > 1 ? `${r.count}× ` : ''}{r.role}
            </span>
          ))}
        </div>
      )}
      {note && (
        <p className="text-[12px] text-violet-700 flex items-start gap-1.5">
          <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{note}</span>
        </p>
      )}
    </div>
  );
}
